import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout1 from "../../Layouts/Layout1";
import LayoutContainer from "../../Layouts/LayoutContainer";
import { ChevronLeft, HelpCircle, Info, Upload } from "lucide-react";
import RentalDataUseModal from "./RentalDataUseModal";
import { toast } from "react-toastify";
import { useRentalProfile } from "../../context/RentalProfileContext";
import { RENTAL_PROFILE_SECTIONS } from "../../Constants/rentalProfile";
import RentalFilePreview from "./RentalFilePreview";

const MAX_CHARS = 3000;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPT =
  "image/gif,image/jpeg,image/jpg,image/png,image/heic,image/heif,application/pdf,.pdf";

const textareaClass =
  "min-h-[180px] w-full resize-y rounded-lg border border-[#E5E7EB] bg-white px-3 py-3 text-sm text-[#1A1A1A] outline-none transition-shadow placeholder:text-[#9CA3AF] focus:border-[#097FE6] focus:ring-2 focus:ring-[#097FE6]/20";

const AboutMe = () => {
  const navigate = useNavigate();
  const { profile, loading, saving, updateSection, uploadDocument } =
    useRentalProfile();
  const fileInputRef = useRef(null);
  const [dataUseModalOpen, setDataUseModalOpen] = useState(false);
  const [aboutText, setAboutText] = useState("");
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (loading) return;
    setAboutText(profile.aboutMe.aboutText || "");
    setFile(null);
  }, [loading, profile.aboutMe]);

  const handleAboutChange = (e) => {
    const next = e.target.value;
    if (next.length <= MAX_CHARS) setAboutText(next);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > MAX_FILE_BYTES) {
      toast.error("File must be 10MB or smaller.");
      return;
    }
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let supportingDocument = profile.aboutMe.supportingDocument || null;

    if (file) {
      supportingDocument = await uploadDocument(file);
      if (!supportingDocument) return;
    }

    const ok = await updateSection(RENTAL_PROFILE_SECTIONS.ABOUT_ME, {
      aboutText: aboutText.trim(),
      supportingDocument,
    });
    if (ok) navigate("/rental-applications");
  };

  return (
    <Layout1>
      <LayoutContainer className="max-w-xl py-8 md:py-12 lg:py-14">
        <button
          type="button"
          onClick={() => navigate("/rental-applications")}
          className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-[#097FE6] hover:underline"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2} />
          Back
        </button>

        <h1 className="text-2xl font-bold tracking-tight text-[#000929] md:text-3xl">
          About me
        </h1>

        <div className="mt-8 rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm md:p-8">
          <form onSubmit={handleSubmit} className="space-y-10">
            <section>
              <h2 className="text-base font-semibold text-[#000929]">
                Introduce yourself
              </h2>
              <p className="mt-1.5 text-sm leading-relaxed text-[#6B7280]">
                Share with the agent and landlord why you are the best fit for
                the property.
              </p>
              <div className="mt-4">
                <textarea
                  id="about-me-text"
                  value={aboutText}
                  onChange={handleAboutChange}
                  placeholder="Include details about yourself, any hobbies and why you want this property."
                  className={textareaClass}
                  rows={8}
                  maxLength={MAX_CHARS}
                />
                <p className="mt-2 text-xs text-[#6B7280]">
                  {aboutText.length}/{MAX_CHARS}
                </p>
              </div>
              <div className="mt-4 flex gap-3 rounded-xl bg-[#EBF5FF] px-4 py-3.5">
                <Info
                  className="mt-0.5 h-5 w-5 shrink-0 text-[#097FE6]"
                  strokeWidth={2}
                />
                <p className="text-sm leading-relaxed text-[#1A1A1A]">
                  To stand out, provide a few extra details about yourself. Not
                  sure how to write an &apos;about me&apos;?{" "}
                  <Link
                    to="/blog"
                    className="font-medium text-[#097FE6] hover:underline"
                  >
                    View our how to guide.
                  </Link>
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-base font-semibold text-[#000929]">
                Optional supporting documents
              </h2>
              <p className="mt-1.5 text-sm leading-relaxed text-[#6B7280]">
                Attach any supporting document you&apos;d like e.g. letters of
                recommendation, tenant ledgers or company guarantees.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={handleFileChange}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-[#E5E7EB] bg-white py-3 text-sm font-medium text-[#1A1A1A] transition-colors hover:bg-[#F9FAFB]"
              >
                <Upload className="h-5 w-5 text-[#097FE6]" strokeWidth={2} />
                Upload a file
              </button>
              <RentalFilePreview
                file={file}
                saved={file ? null : profile.aboutMe.supportingDocument}
                onRemove={file ? () => setFile(null) : undefined}
              />
              <p className="mt-2 text-xs leading-relaxed text-[#9CA3AF]">
                Max. 10MB - GIF, JPG, JPEG, PNG, HEIC, PDF
              </p>
            </section>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading || saving}
                className="w-full rounded-lg bg-[#097FE6] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#0868c4] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save and back"}
              </button>
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => navigate("/rental-applications")}
                  className="text-sm font-medium text-[#097FE6] hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>

        <button
          type="button"
          onClick={() => setDataUseModalOpen(true)}
          className="mt-10 inline-flex items-center gap-2 text-sm text-[#6B7280] underline decoration-[#6B7280] underline-offset-2 hover:text-[#1A1A1A]"
        >
          <HelpCircle className="h-4 w-4 shrink-0" strokeWidth={2} />
          How this information is used
        </button>
      </LayoutContainer>

      <RentalDataUseModal
        variant="personalDetails"
        open={dataUseModalOpen}
        onClose={() => setDataUseModalOpen(false)}
      />
    </Layout1>
  );
};

export default AboutMe;
